import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { DeviceFingerprintService } from './device-fingerprint.service';

export type MfaChannel = 'totp' | 'email' | 'sms';

export interface MfaStatus {
  enabled: boolean;
  channel?: MfaChannel;
  enrolled_at?: string;
  backup_codes_remaining: number;
  totp_available?: boolean;
  phone_available?: boolean;
  email_available?: boolean;
}

export interface MfaChallenge {
  mfa_required: true;
  challenge_id: string;
  method: MfaChannel;
  destination_hint?: string;
  reason?: string;
  expires_at: string;
  totp_available?: boolean;
  phone_available?: boolean;
  email_available?: boolean;
}

export interface TrustedDeviceEntry {
  id: number;
  fingerprint: string;
  geo_country?: string;
  geo_city?: string;
  user_agent?: string;
  mfa_verified_at: string;
  expires_at: string;
  last_used_at?: string;
}

@Injectable({ providedIn: 'root' })
export class MfaService {
  constructor(
    private api: ApiService,
    private fingerprint: DeviceFingerprintService
  ) {}

  status(): Observable<{ data: MfaStatus; status: number }> {
    return this.api.get('/user/mfa/status');
  }

  enrollTotpInit(): Observable<any> {
    return this.api.post('/user/mfa/enroll/totp/init', {});
  }

  enrollTotpConfirm(code: string): Observable<any> {
    return this.api.post('/user/mfa/enroll/totp/confirm', { code });
  }

  enrollOtpInit(channel: 'email' | 'sms'): Observable<any> {
    return this.api.post(`/user/mfa/enroll/${channel}/init`, {});
  }

  enrollOtpConfirm(channel: 'email' | 'sms', challengeId: string, code: string): Observable<any> {
    return this.api.post(`/user/mfa/enroll/${channel}/confirm`, {
      challenge_id: challengeId,
      code
    });
  }

  /**
   * Initialize an email or SMS code dispatch for a sensitive action (disable / regenerate).
   * Returns { challenge_id, method, expires_at }.
   */
  actionChallenge(method: 'email' | 'sms', purpose: 'disable' | 'enroll' = 'disable'): Observable<any> {
    return this.api.post('/user/mfa/challenge', { method, purpose });
  }

  disable(password: string, code: string, challengeId?: string): Observable<any> {
    const body: any = { password, code };
    if (challengeId) body.challenge_id = challengeId;
    return this.api.post('/user/mfa/disable', body);
  }

  regenerateBackupCodes(password: string, code: string, challengeId?: string): Observable<any> {
    const body: any = { password, code };
    if (challengeId) body.challenge_id = challengeId;
    return this.api.post('/user/mfa/backup-codes/regenerate', body);
  }

  listDevices(): Observable<any> {
    return this.api.get('/user/mfa/devices');
  }

  revokeDevice(id: number): Observable<any> {
    return this.api.delete(`/user/mfa/devices/${id}`);
  }

  verifyLogin(challengeId: string, code: string, trustDevice: boolean): Observable<any> {
    return this.api.post('/user/login/mfa/verify', {
      challenge_id: challengeId,
      code,
      trust_device: trustDevice ? 1 : 0,
      device_fingerprint: this.fingerprint.get()
    });
  }

  resendLoginCode(challengeId: string, method?: 'email' | 'sms'): Observable<any> {
    const body: any = { challenge_id: challengeId };
    if (method) body.method = method;
    return this.api.post('/user/login/mfa/resend', body);
  }

  adminStatus(imei: string): Observable<any> {
    return this.api.get(`/user/mfa/admin/${imei}/status`);
  }

  adminDisable(imei: string, password: string): Observable<any> {
    return this.api.post(`/user/mfa/admin/${imei}/disable`, { password });
  }
}
